import {
  ANXIETY_UNIT_LABELS,
  type AnxietyUnitView,
} from "@/lib/anxietyView";
import {
  describeCriterionResult,
  describeOperator,
  formatScalar,
} from "@/lib/previewDecisions";
import { Badge } from "@/ui/Badge";

/**
 * The Anxiety view: for each anxiety unit the run touched, the steps the
 * runtime evaluated and the threshold each was held to.
 *
 * Every value is the producer's own — the observed value, the operator, the
 * threshold and the verdict — carried from the run's criterion events, and the
 * table shows the LATEST evaluation of each step with the turn it happened on
 * and how many times the run evaluated it. Nothing is computed here, and a
 * step the run never evaluated is simply not a row: absence is not "unmet".
 */
export function AnxietyView({ units }: { units: readonly AnxietyUnitView[] }) {
  if (units.length === 0) {
    return null;
  }
  return (
    <section aria-labelledby="anxiety-view-heading" className="space-y-3">
      <div className="space-y-1">
        <h3 id="anxiety-view-heading" className="text-base font-semibold">
          Anxiety view
        </h3>
        <p className="max-w-3xl text-sm text-muted-foreground">
          The anxiety steps this run evaluated, by unit, with the threshold each
          was held to. Values are the runtime&apos;s own; each row shows the
          latest evaluation.
        </p>
      </div>
      {units.map((unit) => (
        <div
          key={unit.unit}
          className="overflow-x-auto rounded-md border border-border"
        >
          <table className="w-full text-sm">
            <caption className="flex flex-wrap items-baseline gap-2 border-b border-border bg-muted/40 px-3 py-2 text-start text-sm font-medium text-foreground">
              <span>{ANXIETY_UNIT_LABELS[unit.unit]}</span>
              <span className="text-xs font-normal text-muted-foreground">
                {unit.classifications === 0
                  ? "no classification recorded"
                  : `${unit.classifications} classification${unit.classifications === 1 ? "" : "s"}${
                      unit.lastClassificationTurn === null
                        ? ""
                        : `, latest on turn ${unit.lastClassificationTurn}`
                    }`}
              </span>
            </caption>
            <thead>
              <tr className="text-start text-xs uppercase tracking-wide text-muted-foreground">
                <th scope="col" className="px-3 py-2 font-medium">
                  Step
                </th>
                <th scope="col" className="px-3 py-2 font-medium">
                  Observed
                </th>
                <th scope="col" className="px-3 py-2 font-medium">
                  Threshold
                </th>
                <th scope="col" className="px-3 py-2 font-medium">
                  Result
                </th>
                <th scope="col" className="px-3 py-2 font-medium">
                  Turn
                </th>
              </tr>
            </thead>
            <tbody>
              {unit.steps.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-3 py-3 text-muted-foreground"
                  >
                    No threshold step was evaluated for this unit.
                  </td>
                </tr>
              ) : (
                unit.steps.map((row) => (
                  <tr
                    key={row.criterionId}
                    className="border-t border-border align-top"
                  >
                    <th
                      scope="row"
                      className="px-3 py-2 text-start font-normal text-foreground"
                    >
                      <span dir="ltr">{row.step}</span>
                      {row.isVerdict ? (
                        <span className="ms-2 text-xs text-muted-foreground">
                          verdict
                        </span>
                      ) : null}
                    </th>
                    <td className="px-3 py-2" dir="auto">
                      {formatScalar(row.observed)}
                    </td>
                    <td className="px-3 py-2" dir="auto">
                      {describeOperator(row.operator)}{" "}
                      <span dir="auto">{formatScalar(row.threshold)}</span>
                    </td>
                    <td className="px-3 py-2">
                      <Badge tone={row.passed ? "success" : "outline"}>
                        {describeCriterionResult(row.passed)}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      <span dir="ltr">
                        {row.turnNumber === null ? "—" : row.turnNumber}
                      </span>
                      {row.evaluations > 1 ? (
                        <span className="ms-1 text-xs">
                          ({row.evaluations} evaluations)
                        </span>
                      ) : null}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ))}
    </section>
  );
}
